/**
 * ImageProofDropzone.tsx
 *
 * Reusable image-proof capture control supporting three input modes:
 * click-to-browse, drag-and-drop, and clipboard paste. Used by both the
 * authenticated `TerminationChecklistList` and the public
 * `HotlinkConfirmPage` to collect the proof images required by the
 * `social_insurance_closing` termination task type.
 *
 * This component only picks a file and shows a local preview — it does not
 * upload anything itself. Callers receive the chosen `File` via `onFileReady`
 * and are responsible for submitting it.
 */

import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export interface ImageProofDropzoneProps {
    /** Called with the newly selected file (click, drop, or paste). */
    onFileReady: (file: File) => void;
    /** URL of a previously uploaded proof image to show instead of the empty prompt. */
    existingImageUrl?: string;
    /** Disables all interaction (e.g. while an upload is in flight). */
    disabled?: boolean;
    /** Short label shown above the dropzone (e.g. "Processing proof"). */
    label?: string;
}

/**
 * A click/drag-drop/paste image picker with local preview.
 *
 * @example
 * <ImageProofDropzone label="Processing proof" onFileReady={setFile} />
 */
export const ImageProofDropzone: React.FC<ImageProofDropzoneProps> = ({
    onFileReady,
    existingImageUrl,
    disabled = false,
    label,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const acceptFile = useCallback((file: File | null | undefined) => {
        if (!file || !ACCEPTED_MIME_TYPES.includes(file.type)) return;
        setPreviewUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        onFileReady(file);
    }, [onFileReady]);

    const handleClick = () => {
        if (!disabled) inputRef.current?.click();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        acceptFile(e.target.files?.[0]);
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        acceptFile(e.dataTransfer.files?.[0]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of Array.from(items)) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file && ACCEPTED_MIME_TYPES.includes(file.type)) {
                    acceptFile(file);
                    return;
                }
            }
        }
    };

    const displayUrl = previewUrl ?? existingImageUrl ?? null;

    return (
        <Box>
            {label && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            <Box
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={handleClick}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onPaste={handlePaste}
                data-testid="image-proof-dropzone"
                sx={{
                    border: '2px dashed',
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    background: isDragging ? 'action.hover' : 'transparent',
                    outline: 'none',
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_MIME_TYPES.join(',')}
                    onChange={handleInputChange}
                    disabled={disabled}
                    style={{ display: 'none' }}
                    data-testid="image-proof-file-input"
                />
                {displayUrl ? (
                    <Box
                        component="img"
                        src={displayUrl}
                        alt="Proof preview"
                        sx={{ maxWidth: '100%', maxHeight: 160, borderRadius: 1 }}
                    />
                ) : (
                    <>
                        <CloudUploadIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                            Click to browse, drag & drop, or paste an image
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
};
